import type { ReactNode } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  OctagonAlert,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../lib/cn";
import { Button } from "./Button";

/* -------------------------------------------------------------------------- */
/* Spinner                                                                     */
/* -------------------------------------------------------------------------- */

export function Spinner({
  size = 20,
  className,
  label = "Loading",
}: {
  size?: number;
  className?: string;
  label?: string;
}) {
  return (
    <Loader2
      size={size}
      role="status"
      aria-label={label}
      className={cn("animate-spin text-gold-500", className)}
    />
  );
}

/** Full-panel loading state with the brand ring animation. */
export function LoadingState({
  message = "Loading…",
  className,
}: {
  message?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-56 flex-col items-center justify-center gap-4 py-12",
        className,
      )}
    >
      <span className="relative grid size-12 place-items-center">
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border-2 border-gold-500/40 animate-[pulse-ring_2.4s_cubic-bezier(0.25,1,0.5,1)_infinite]"
        />
        <Spinner size={26} />
      </span>
      <p className="text-sm text-content-muted">{message}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Skeletons                                                                   */
/* -------------------------------------------------------------------------- */

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "block rounded-md bg-[linear-gradient(90deg,var(--ir-surface-sunken)_25%,var(--ir-line)_37%,var(--ir-surface-sunken)_63%)]",
        "bg-[length:200%_100%] animate-shimmer",
        className,
      )}
    />
  );
}

export function SkeletonTable({
  rows = 6,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <div className="space-y-2 p-1" aria-hidden="true">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn("h-9 flex-1", columnIndex === 0 && "max-w-28")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Alert                                                                       */
/* -------------------------------------------------------------------------- */

export type AlertTone = "info" | "success" | "warning" | "danger";

const alertStyles: Record<AlertTone, { wrap: string; icon: LucideIcon; iconClass: string }> = {
  info: {
    wrap: "border-info-500/25 bg-info-50 text-info-700 dark:bg-info-500/10 dark:text-info-500",
    icon: Info,
    iconClass: "text-info-500",
  },
  success: {
    wrap: "border-success-500/25 bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-500",
    icon: CheckCircle2,
    iconClass: "text-success-500",
  },
  warning: {
    wrap: "border-warning-500/25 bg-warning-50 text-warning-700 dark:bg-warning-500/10 dark:text-warning-500",
    icon: AlertTriangle,
    iconClass: "text-warning-500",
  },
  danger: {
    wrap: "border-danger-500/25 bg-danger-50 text-danger-700 dark:bg-danger-500/10 dark:text-danger-500",
    icon: OctagonAlert,
    iconClass: "text-danger-500",
  },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function Alert({
  tone = "info",
  title,
  children,
  action,
  className,
}: AlertProps) {
  const { wrap, icon: Icon, iconClass } = alertStyles[tone];

  return (
    <motion.div
      role="alert"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "flex items-start gap-3 rounded-xl border px-4 py-3 text-sm",
        wrap,
        className,
      )}
    >
      <Icon size={17} aria-hidden="true" className={cn("mt-0.5 shrink-0", iconClass)} />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "opacity-95")}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty and error states                                                      */
/* -------------------------------------------------------------------------- */

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      {Icon && (
        <span className="grid size-14 place-items-center rounded-2xl border border-line bg-surface-sunken text-content-subtle">
          <Icon size={24} aria-hidden="true" />
        </span>
      )}
      <div className="max-w-sm">
        <h3 className="text-base font-semibold text-content">{title}</h3>
        {description && (
          <p className="mt-1 text-sm text-content-muted">{description}</p>
        )}
      </div>
      {action}
    </motion.div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
  className,
}: {
  title?: string;
  message?: ReactNode;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      icon={OctagonAlert}
      title={title}
      description={message}
      className={className}
      action={
        onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Progress                                                                    */
/* -------------------------------------------------------------------------- */

export function ProgressBar({
  value,
  max = 100,
  tone = "gold",
  label,
  className,
}: {
  value: number;
  max?: number;
  tone?: "gold" | "crimson";
  label?: string;
  className?: string;
}) {
  const percent = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn(
        "h-2 w-full overflow-hidden rounded-full bg-surface-sunken",
        className,
      )}
    >
      <motion.div
        className={cn(
          "h-full rounded-full",
          tone === "gold" ? "bg-gold-sheen" : "bg-crimson-sheen",
        )}
        initial={{ width: 0 }}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}
